import { chatDrawerActions, chatDrawerSelectors } from 'ducks/chatDrawer'
import { BetaChip } from 'elements/chip/BetaChip'
import { CloseIcon, Icon, IconButton, MoreOptionsIcon, styled } from 'opensolar-ui'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdaIcon from './AdaIcon'
import { ChatHeaderMenu } from './ChatHeaderMenu'

type AnchorPosition = 'left' | 'top' | 'right' | 'bottom'
type DisplayMode = 'drawer' | 'dialog'

interface ChatHeaderProps {
  displayMode: DisplayMode
  anchor: AnchorPosition
  onDisplayModeChange: (mode: DisplayMode) => void
  onAnchorChange: (position: AnchorPosition) => void
  onClearChat: () => void
  onClose?: () => void
  enableGeminiLiveAPI?: boolean
}

const ChatHeaderContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  backgroundColor: '#ffffff',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  minHeight: 'auto',
})

const ChatTitle = styled('div')({
  fontSize: 18,
  fontWeight: 600,
  color: '#1976d2',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
})

const HeaderControls = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const HeaderButton = styled(IconButton)({
  padding: 8,
  color: '#666666',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: '#333333',
  },
})

const AdaIconWrapper = styled('div')({
  marginRight: 12,
})

const AdaLabelWrapper = styled('div')({
  marginRight: 12,
})

const ChatHeader: React.FC<ChatHeaderProps> = ({
  displayMode,
  anchor,
  onDisplayModeChange,
  onAnchorChange,
  onClearChat,
  onClose,
  enableGeminiLiveAPI = false,
}) => {
  const dispatch = useDispatch()
  const chatHeaderMenuOpen = useSelector(chatDrawerSelectors.getChatHeaderMenuOpen)
  const { tour } = useSelector((state: any) => state.tour)
  const isTourActive = tour === 'ada_chat'

  const [layoutMenuAnchorEl, setLayoutMenuAnchorEl] = useState<null | HTMLElement>(null)

  const handleLayoutMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isTourActive) return
    setLayoutMenuAnchorEl(event.currentTarget)
    dispatch(chatDrawerActions.setChatHeaderMenuOpen(true))
  }

  const handleLayoutMenuClose = () => {
    setLayoutMenuAnchorEl(null)
    dispatch(chatDrawerActions.setChatHeaderMenuOpen(false))
  }

  useEffect(() => {
    if (chatHeaderMenuOpen && !layoutMenuAnchorEl) {
      const menuButton = document.getElementById('Ada-Chat-Menu-Button')
      if (menuButton) {
        setLayoutMenuAnchorEl(menuButton)
      }
    } else if (!chatHeaderMenuOpen && layoutMenuAnchorEl) {
      setLayoutMenuAnchorEl(null)
    }
  }, [chatHeaderMenuOpen, layoutMenuAnchorEl])

  return (
    <ChatHeaderContainer id="Ada-Chat-Header">
      <ChatTitle>
        <AdaIconWrapper>
          <AdaIcon size={26} />
        </AdaIconWrapper>
        <AdaLabelWrapper>Ada</AdaLabelWrapper>
        <BetaChip />
      </ChatTitle>
      <HeaderControls>
        <HeaderButton
          id="Ada-Chat-Menu-Button"
          size="small"
          onClick={handleLayoutMenuOpen}
          aria-label="Layout options"
          disabled={isTourActive}
        >
          <MoreOptionsIcon />
        </HeaderButton>
        <HeaderButton
          size="small"
          onClick={() => onDisplayModeChange(displayMode === 'dialog' ? 'drawer' : 'dialog')}
          aria-label={displayMode === 'dialog' ? 'Minimize' : 'Maximize'}
          disabled={isTourActive}
        >
          <Icon icon={displayMode === 'dialog' ? 'fullscreen_exit' : 'fullscreen'} size="small" />
        </HeaderButton>
        <HeaderButton
          size="small"
          onClick={onClose || (() => dispatch(chatDrawerActions.setChatDrawerOpen(false)))}
          aria-label="Close"
          disabled={isTourActive}
        >
          <CloseIcon variant={1} />
        </HeaderButton>
      </HeaderControls>

      <ChatHeaderMenu
        anchorEl={layoutMenuAnchorEl}
        onClose={handleLayoutMenuClose}
        displayMode={displayMode}
        anchor={anchor}
        onDisplayModeChange={onDisplayModeChange}
        onAnchorChange={onAnchorChange}
        onClearChat={onClearChat}
        enableGeminiLiveAPI={enableGeminiLiveAPI}
      />
    </ChatHeaderContainer>
  )
}

export default ChatHeader
